import { useState } from 'react';
import { all } from '@/api/fee/charge';
import { Button } from '@/components/ui/button';
import { SearchBar } from '@/components/ui/SearchBar/SearchBar';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { useSearch } from '@/hooks/use-search';
import { keepPreviousData, Updater, useQuery } from '@tanstack/react-query';
import {
	ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
	VisibilityState,
} from '@tanstack/react-table';
import {
	FileSpreadsheet,
	LoaderCircle,
} from 'lucide-react';

import { columns } from './columns';
import { AddCharge } from './sheet-add';

export function ChargeTable() {
	const { searchTerm, setSearchTerm, pagination, setPagination } = useSearch();
	const [sorting, setSorting] = useState<SortingState>([
		{
			id: 'id',
			desc: false,
		},
	]);
	const { data, isFetching } = useQuery({
		queryFn: () =>
			all(pagination, sorting, {
				searchTerm: searchTerm,
			}),
		queryKey: ['charges', pagination, sorting, searchTerm],
		placeholderData: keepPreviousData,
		staleTime: 30000,
	});

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = useState({});
	const [globalFilter, setGlobalFilter] = useState<any>([]);
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const handlePageChange = (
		updater: Updater<PaginationState, PaginationState>,
	) => {
		setPagination((prev) => {
			const newPagination =
				typeof updater === 'function' ? updater(prev) : updater;

			return newPagination; // Allow page change
		});
	};
	const table = useReactTable({
		data: data?.data ?? [],
		columns: columns,
		rowCount: data?.total ?? 0,
		manualPagination: true,
		manualSorting: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		onGlobalFilterChange: setGlobalFilter,
		onPaginationChange: handlePageChange,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			globalFilter,
			pagination,
		},
	});

	return (
		<div className="h-1/2 w-full">
			<div className="flex items-center justify-end gap-4 py-4">
				<SearchBar
					placeholder="Search..."
					onSearch={setSearchTerm}
					isLoading={isFetching}
				/>
				<div className="flex items-center gap-2">
					<AddCharge />
				</div>
			</div>
			{isFetching ? (
				<div className="flex items-center justify-center py-10">
					<LoaderCircle size={40} className="animate-spin" />
				</div>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<TableHead key={header.id}>
												{header.isPlaceholder
													? null
													: flexRender(header.column.columnDef.header, header.getContext())}
											</TableHead>
										);
									})}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{table.getRowModel().rows?.length ? (
								table.getRowModel().rows.map((row) => (
									<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={columns.length} className="h-24 text-center">
										No results.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			)}

			<TablePagination table={table} isFetching={isFetching} />
		</div>
	);
}
